<script async setup lang="ts">
import BreadcrumbComponent from '@/components/custom/ui/BreadcrumbComponent.vue';
import { Carousel, Slide, Navigation } from 'vue3-carousel';

import { useEventStore } from '@/stores/events.store';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { EventEntity } from '@/entities/interfaces/events';
import { getPhotos } from '@/entities/api/photos';
import { PhotoEntity } from '@/entities/interfaces/photos';
import { dateToText } from '@/helpers/dateToText';
import { getImageLink } from '@/helpers/imageLink';
import DownloadIcon from '@/components/custom/ui/DownloadIcon.vue';
import axios from 'axios';

const eventStore = useEventStore();
const route = useRoute();

const id = computed(() => route.params.id as string);
const event = computed<EventEntity>(
  () =>
    eventStore.getEvents.value?.filter(
      (event: EventEntity) => event.id == id.value
    )[0]
);
const date = computed(() => {
  const date = dateToText(event.value.plannedDateTime).split(' ');
  return `${date[0]} ${date[1]}`;
});

const photos = ref<PhotoEntity[]>();
const activePhoto = ref<number | undefined>();

watch(event, () => {
  if (!event.value?.id) return;
  getPhotos(event.value.id).then((images) => (photos.value = images));
});

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  if (!event.value?.id) return;
  getPhotos(event.value.id).then((images) => (photos.value = images));
});

const setPhoto = (i: number | undefined) => {
  activePhoto.value = i;
};

const downloadAll = () => {
  photos.value?.forEach((photo) => {
    download(photo.imageId);
  });
};

const download = async (image: string) => {
  try {
    const link = getImageLink(image);
    const a = document.createElement('a');

    const res = await axios.get(link, { responseType: 'blob' });
    const ext = res.headers['content-type'];

    const url = URL.createObjectURL(res.data as Blob);
    a.download = `${image}.${ext?.split('/')[1]}`;
    a.href = url;

    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 0);
  } catch (err) {
    console.error(err);
  }
};
</script>

<template>
  <section class="photos" v-if="event">
    <div class="photos-header">
      <BreadcrumbComponent
        :paths="[
          { name: 'Главная страница', link: '/' },
          { name: 'Фотогалерея', link: '/gallery' }
        ]"
      />
      <time>{{ date }}</time>
      <div class="photos-title">
        <h2>{{ event.name }}</h2>

        <a @click="downloadAll()">
          <DownloadIcon color="#153fc8" />
          <p>Скачать все ({{ photos?.length }})</p>
        </a>
      </div>
    </div>

    <div class="photos-list">
      <div class="photo" v-for="(photo, index) in photos" :key="photo.id">
        <a @click="download(photo.imageId)" class="photo-download">
          <DownloadIcon color="#27303E" />
        </a>
        <div class="photo-wrapper" @click="setPhoto(index)">
          <img :src="getImageLink(photo.imageId)" alt="" />
        </div>
      </div>
    </div>

    <div class="active-photo" v-if="activePhoto != undefined">
      <div class="photo-slider">
        <Carousel
          :items-to-show="1"
          snapAlign="center"
          :modelValue="activePhoto"
        >
          <Slide v-for="photo in photos" :key="photo.id">
            <div class="photo-wrapper">
              <img :src="getImageLink(photo.imageId)" alt="" />
            </div>
          </Slide>

          <template #addons>
            <Navigation />
          </template>
        </Carousel>
        <div class="photo-slider-buttons">
          <button @click="setPhoto(undefined)">
            <img src="/assets/icons/close.svg" alt="Закрыть" />
          </button>
          <a
            v-if="photos"
            @click="download(photos[activePhoto].imageId)"
            class="photo-download"
          >
            <DownloadIcon color="#27303E" />
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.photos {
  &-header {
    display: flex;
    flex-direction: column;

    h2 {
      font-size: var(--font-size-lm);
    }

    time {
      padding-bottom: 24px;
      font-size: var(--font-size-l);
      color: var(--color-mono-400);
      font-weight: var(--font-weight-regular);
    }
  }

  &-title {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media screen and (max-width: 1024px) {
      gap: 24px;
      align-items: flex-start;
      flex-direction: column;
    }

    a {
      gap: 12px;
      display: flex;
      color: #153fc8;
      align-items: center;
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-light);
    }
  }

  &-filters {
    gap: 12px;
    display: flex;
    flex-wrap: wrap;
    scrollbar-width: none;
    -ms-overflow-style: none;

    @media screen and (max-width: 1024px) {
      flex-wrap: nowrap;
      overflow-x: auto;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &-list {
    gap: 40px;
    width: 100%;
    height: auto;
    display: flex;
    flex-wrap: wrap;
    padding: 60px 0;

    .photo {
      flex: 1 440px;
      position: relative;
      border-radius: 30px;
      background: var(--color-mono-50);

      @media screen and (min-width: 1600px) {
        max-width: 440px;
      }

      &-download {
        z-index: 1;
        top: 20px;
        right: 20px;
        width: 48px;
        height: 48px;
        display: flex;
        position: absolute;
        align-items: center;
        border-radius: 20px;
        justify-content: center;
        background: var(--color-mono-0);
      }
    }
  }
}

.photo-wrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  aspect-ratio: 440 / 300;

  img {
    width: 100%;
  }
}

.active-photo {
  top: 0;
  left: 0;
  z-index: 5;
  width: 100%;
  height: 100%;
  position: fixed;

  display: flex;
  align-items: center;
  justify-content: center;
  background: #27303e4d;

  .photo-slider {
    position: relative;
    width: calc(80vw - 160px);
    max-width: 1200px;

    &-buttons {
      top: 0;
      right: 0;
      gap: 8px;
      display: flex;
      position: absolute;
      flex-direction: column;
      transform: translateX(calc(100% + 16px));

      button,
      a {
        width: 64px;
        height: 64px;
        border: none;
        cursor: pointer;
        padding: 20px !important;
        border-radius: 20px !important;
        background: var(--color-mono-0);
      }
    }
  }

  .photo-wrapper {
    max-height: 75vh;
    background: var(--color-mono-50);

    img {
      width: auto;
      max-width: 100%;
      max-height: 100%;
    }
  }

  @media screen and (max-width: 1024px) {
    display: none;
  }
}
</style>
